
import { useMemo } from 'react';
import { SiteBooking } from './useSiteBookings';

/**
 * Returns booking state for a specific site for the target date range.
 * - `bookedNow`: boolean if site is currently booked
 * - `nextAvailableDate`: when site is next available (YYYY-MM-DD string | null)
 * - `futureBookings`: up to N future bookings after today
 */
export function useSiteBookingStatus(bookings: SiteBooking[] = [], dateRange?: { start?: Date; end?: Date }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Return details for grid card/map marker color + calendar
  return useMemo(() => {
    const siteId = bookings[0]?.site_id || 'unknown';
    console.log(`[useSiteBookingStatus] Processing ${bookings.length} bookings for site ${siteId}`);
    console.log(`[useSiteBookingStatus] Today is: ${today.toISOString().split('T')[0]}`);
    console.log(`[useSiteBookingStatus] Date range filter:`, dateRange);
    
    if (!Array.isArray(bookings) || !bookings.length) {
      console.log(`[useSiteBookingStatus] No bookings for site ${siteId} - marking as available`);
      return { bookedNow: false, currentBooking: null, nextAvailableDate: null, futureBookings: [], isBookedForDateRange: false };
    }

    // Log all bookings for this site
    bookings.forEach((booking, index) => {
      console.log(`[useSiteBookingStatus] Booking ${index + 1} for site ${siteId}:`, {
        id: booking.id,
        start_date: booking.start_date,
        end_date: booking.end_date,
        customer_name: booking.customer_name,
        payment_status: booking.payment_status
      });
    });

    // Find current booking overlapping today (inclusive of start, exclusive of end)
    const currentBooking = bookings.find(booking => {
      const start = new Date(booking.start_date);
      const end = new Date(booking.end_date);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      
      // Check if today falls within the booking period (start <= today < end)
      const isCurrentlyBooked = start <= today && today < end;
      console.log(`[useSiteBookingStatus] Checking booking ${booking.id} for current status:`, {
        start_date: start.toISOString().split('T')[0],
        end_date: end.toISOString().split('T')[0],
        today: today.toISOString().split('T')[0],
        start_lte_today: start <= today,
        today_lt_end: today < end,
        isCurrentlyBooked
      });
      
      if (isCurrentlyBooked) {
        console.log(`[useSiteBookingStatus] Site ${siteId} is currently booked by booking ${booking.id} from ${booking.start_date} to ${booking.end_date} (customer: ${booking.customer_name})`);
      }
      return isCurrentlyBooked;
    });

    // Find up to 3 upcoming bookings after today
    const futureBookings =
      bookings
        .filter(booking => {
          const start = new Date(booking.start_date);
          start.setHours(0, 0, 0, 0);
          return start > today;
        })
        .sort((a, b) => +new Date(a.start_date) - +new Date(b.start_date))
        .slice(0, 3);

    console.log(`[useSiteBookingStatus] Found ${futureBookings.length} future bookings for site ${siteId}`);

    // Date site next becomes available, if booked now
    let nextAvailableDate: string | null = null;
    if (currentBooking) {
      const currentEnd = currentBooking.end_date;
      const nextBooking = futureBookings.find(b => b.start_date === currentEnd);
      nextAvailableDate = nextBooking ? null : currentEnd;
      console.log(`[useSiteBookingStatus] Site ${siteId} next available date: ${nextAvailableDate}`);
    }

    // If dateRange is set, check if any booking overlaps
    let isBookedForDateRange = false;
    if (dateRange?.start && dateRange?.end) {
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      
      console.log(`[useSiteBookingStatus] Checking date range overlap for site ${siteId}:`, {
        requested_start: startDate.toISOString().split('T')[0],
        requested_end: endDate.toISOString().split('T')[0]
      });
      
      isBookedForDateRange = bookings.some(booking => {
        const bookingStart = new Date(booking.start_date);
        const bookingEnd = new Date(booking.end_date);
        bookingStart.setHours(0, 0, 0, 0);
        bookingEnd.setHours(0, 0, 0, 0);
        
        const overlaps = bookingStart < endDate && bookingEnd > startDate; // range overlap
        console.log(`[useSiteBookingStatus] Checking booking ${booking.id} for date range overlap:`, {
          booking_start: bookingStart.toISOString().split('T')[0],
          booking_end: bookingEnd.toISOString().split('T')[0],
          booking_start_lt_requested_end: bookingStart < endDate,
          booking_end_gt_requested_start: bookingEnd > startDate,
          overlaps
        });
        
        if (overlaps) {
          console.log(`[useSiteBookingStatus] Site ${siteId} booking ${booking.id} overlaps with requested date range`);
        }
        return overlaps;
      });
    }

    const result = {
      bookedNow: !!currentBooking,
      currentBooking: currentBooking || null,
      isBookedForDateRange,
      futureBookings,
      nextAvailableDate,
    };

    console.log(`[useSiteBookingStatus] Final status for site ${siteId}:`, {
      bookedNow: result.bookedNow,
      isBookedForDateRange: result.isBookedForDateRange,
      currentBooking: result.currentBooking?.id,
      currentBookingCustomer: result.currentBooking?.customer_name,
      futureBookingsCount: result.futureBookings.length,
      nextAvailableDate: result.nextAvailableDate
    });

    return result;
  }, [bookings, today, dateRange]);
}
